import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, Trash2, RefreshCw, Share2, Copy, Check, Link2Off } from 'lucide-react';
import { useWishlistDetail, useShareWishlist, useUnshareWishlist } from '../hooks/useWishlists';
import { useDeleteProduct, useRefreshProduct } from '../hooks/useProducts';
import AddProductForm from '../components/AddProductForm';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

function formatPrice(value?: number) {
  if (value == null) return '–';
  return `$${value.toFixed(2)}`;
}

function timeAgo(dateStr?: string) {
  if (!dateStr) return null;
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function WishlistDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: wishlist, isLoading, isError } = useWishlistDetail(id!);
  const deleteProduct = useDeleteProduct();
  const refreshProduct = useRefreshProduct();
  const shareWishlist = useShareWishlist();
  const unshareWishlist = useUnshareWishlist();
  const [copied, setCopied] = useState(false);

  const shareUrl = wishlist?.share_token
    ? `https://priceotter.ai/wishlist/${wishlist.share_token}`
    : null;

  const handleShare = async () => {
    const result = await shareWishlist.mutateAsync(id!);
    await navigator.clipboard.writeText(result.share_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading wishlist...
      </div>
    );
  }

  if (isError || !wishlist) {
    return (
      <div className="space-y-4">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-sm text-destructive">Wishlist not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <div className="flex-1">
          <h2 className="text-2xl font-semibold text-foreground">{wishlist.name}</h2>
          <p className="text-sm text-muted-foreground">
            {wishlist.products.length} {wishlist.products.length === 1 ? 'product' : 'products'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {shareUrl ? (
            <>
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <button
                onClick={() => unshareWishlist.mutate(id!)}
                disabled={unshareWishlist.isPending}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                title="Revoke share link"
              >
                <Link2Off className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={handleShare}
              disabled={shareWishlist.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {shareWishlist.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4" />
              )}
              Share
            </button>
          )}
        </div>
      </div>

      <AddProductForm fixedWishlistId={id} />

      {wishlist.products.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-sm text-muted-foreground">
            No products in this wishlist yet. Add one above to get started.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card divide-y divide-border">
          {wishlist.products.map((product) => (
            <div key={product.id} className="flex items-center justify-between px-6 py-4">
              <div className="h-14 w-14 flex-shrink-0 mr-4 rounded-md bg-muted overflow-hidden">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                ) : product.enrichment_status === 'pending' ? (
                  <div className="h-full w-full animate-pulse bg-gradient-to-r from-muted to-muted-foreground/20" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground text-xs">
                    No img
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <Link to={`/products/${product.id}`} className="font-medium text-card-foreground truncate hover:text-primary hover:underline block">
                  {product.name}
                </Link>
                {(product.category_l1 || product.category_l2 || product.category_l3) && (
                  <p className="text-xs text-muted-foreground truncate">
                    {[product.category_l1, product.category_l2, product.category_l3]
                      .filter(Boolean)
                      .join(' › ')}
                  </p>
                )}
                {product.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {product.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <span>Best: {formatPrice(product.current_best_price)}</span>
                  <span>Target: {formatPrice(product.target_price)}</span>
                  {product.retailer_name && <span>{product.retailer_name}</span>}
                  {product.enrichment_status === 'pending' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Crawling…
                    </span>
                  ) : (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[product.enrichment_status] ?? ''}`}>
                      {product.enrichment_status}
                    </span>
                  )}
                  {product.last_checked_at && (
                    <span className="text-xs text-muted-foreground" title={new Date(product.last_checked_at).toLocaleString()}>
                      Checked {timeAgo(product.last_checked_at)}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-4 flex items-center gap-1">
                {(product.current_best_url || product.normalized_url) && (
                  <a
                    href={product.redirect_url || product.current_best_url || product.normalized_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md p-2 text-muted-foreground hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Buy ${product.name}`}
                    title={product.redirect_url ? 'Affiliate link' : 'Direct link'}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <button
                  onClick={() => refreshProduct.mutate(product.id)}
                  disabled={product.enrichment_status === 'pending'}
                  className="rounded-md p-2 text-muted-foreground hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={`Refresh price for ${product.name}`}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => deleteProduct.mutate(product.id)}
                  disabled={deleteProduct.isPending}
                  className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Delete ${product.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
